/**
 * Auth Context - Authentication state management
 *
 * Provides:
 * - User state (logged in/out)
 * - Auth actions (login, logout, refresh)
 * - Automatic token refresh
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import type { AuthUser } from '@resolve-ai/shared';
import {
  authApi,
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  ApiError,
} from '../lib/api';

interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextType extends AuthState {
  login: (token: string) => Promise<void>;
  exchangeOAuthCode: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  sendMagicLink: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Token refresh interval (50 minutes - before 1 hour expiry)
const REFRESH_INTERVAL_MS = 50 * 60 * 1000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
  });

  // Refresh tokens
  const refreshAuth = useCallback(async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    try {
      const response = await authApi.refreshTokens(refreshToken);
      setTokens(response.accessToken, response.refreshToken);
      setState({
        user: response.user,
        isLoading: false,
        isAuthenticated: true,
      });
      return true;
    } catch {
      clearTokens();
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      });
      return false;
    }
  }, []);

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      // Check localStorage for tokens
      const accessToken = getAccessToken();
      if (!accessToken) {
        setState({ user: null, isLoading: false, isAuthenticated: false });
        return;
      }

      // Step 3: Verify token with backend
      try {
        const user = await authApi.getCurrentUser();
        setState({ user, isLoading: false, isAuthenticated: true });
      } catch (error) {
        // Token might be expired, try to refresh
        if (error instanceof ApiError && error.code === 'auth_token_expired') {
          const refreshed = await refreshAuth();
          if (!refreshed) {
            clearTokens();
          }
        } else {
          clearTokens();
          setState({ user: null, isLoading: false, isAuthenticated: false });
        }
      }
    };

    initAuth();
  }, [refreshAuth]);

  // Set up token refresh interval
  useEffect(() => {
    if (!state.isAuthenticated) {
      return;
    }

    const intervalId = setInterval(() => {
      refreshAuth();
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [state.isAuthenticated, refreshAuth]);

  // Login with magic link token
  const login = useCallback(async (token: string) => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const response = await authApi.verifyMagicLink(token);
      setTokens(response.accessToken, response.refreshToken);
      setState({
        user: response.user,
        isLoading: false,
        isAuthenticated: true,
      });
    } catch (error) {
      setState({ user: null, isLoading: false, isAuthenticated: false });
      throw error;
    }
  }, []);

  // Exchange OAuth transfer code for tokens
  const exchangeOAuthCode = useCallback(async (code: string) => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const response = await authApi.exchangeCode(code);
      setTokens(response.accessToken, response.refreshToken);
      setState({
        user: response.user,
        isLoading: false,
        isAuthenticated: true,
      });
    } catch (error) {
      setState({ user: null, isLoading: false, isAuthenticated: false });
      throw error;
    }
  }, []);

  // Logout
  const logout = useCallback(async () => {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      try {
        await authApi.logout(refreshToken);
      } catch {
        // Ignore logout errors - just clear local state
      }
    }

    clearTokens();
    setState({ user: null, isLoading: false, isAuthenticated: false });
  }, []);

  // Send magic link
  const sendMagicLink = useCallback(async (email: string) => {
    await authApi.sendMagicLink(email);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        exchangeOAuthCode,
        logout,
        sendMagicLink,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
